import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Loader2 } from '@/design-system/icons';
import {
  Dropdown,
  DropdownTriggerButton,
  DropdownContent,
} from '@/components/Dropdown';
import {
  useNotifications,
  useMarkNotificationRead,
  useDeleteNotification,
} from '../hooks/useNotifications';
import { JoinRequestNotification } from './JoinRequestNotification';
import { JoinRequestRejectedNotification } from './JoinRequestRejectedNotification';
import { GenericNotification } from './GenericNotification';
import { getNotificationState } from '../types';
import type { NotificationItem } from '../types';

// Picks the bespoke component for channels this frontend knows, GenericNotification for the rest
function NotificationRow({
  notification,
  onClick,
  onDelete,
}: {
  notification: NotificationItem;
  onClick?: () => void;
  onDelete?: () => void;
}) {
  switch (notification.channel) {
    case 'join_request':
      return <JoinRequestNotification notification={notification} onClick={onClick} />;
    case 'join_request_rejected':
      return <JoinRequestRejectedNotification notification={notification} onClick={onClick} />;
    default:
      return (
        <GenericNotification
          notification={notification}
          onClick={onClick}
          onDelete={onDelete}
        />
      );
  }
}

export function NotificationCenter() {
  const { t } = useTranslation();

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useNotifications();

  const markReadMutation = useMarkNotificationRead();
  const deleteMutation = useDeleteNotification();

  // The center sits in the app header and re-renders on every route change, so the pages are only
  // flattened and counted when a fetch actually replaces them
  const notifications = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  );

  const unreadCount = useMemo(
    () => notifications.filter((n) => getNotificationState(n) === 'unseen').length,
    [notifications]
  );

  const handleNotificationClick = (notification: NotificationItem) => {
    if (getNotificationState(notification) === 'unseen') {
      markReadMutation.mutate(notification.id);
    }
  };

  const handleDelete = (notificationId: string) => {
    deleteMutation.mutate(notificationId);
  };

  return (
    <Dropdown>
      <DropdownTriggerButton
        className="relative size-9 inline-flex justify-center items-center gap-x-2 rounded-full border-0 shadow-none text-muted hover:bg-surface-darker focus:outline-none focus:bg-surface-darker"
        aria-label={t('header.notifications')}
      >
        <Bell className="size-4" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 size-2 rounded-full bg-primary" />
        )}
      </DropdownTriggerButton>

      <DropdownContent className="w-80 sm:w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-onsurface">
            {t('notifications.title')}
          </h2>
        </div>


        <div className="max-h-80 overflow-y-auto w-[400px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-5 animate-spin text-muted" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted">{t('notifications.empty')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                  onDelete={() => handleDelete(notification.id)}
                />
              ))}

              {hasNextPage && (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="w-full py-3 text-sm text-primary hover:bg-surface-darker transition-colors disabled:opacity-50"
                >
                  {isFetchingNextPage ? (
                    <Loader2 className="size-4 animate-spin mx-auto" />
                  ) : (
                    t('notifications.loadMore')
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </DropdownContent>
    </Dropdown>
  );
}
